import {
	filter,
	isNumber,
	map,
	maxBy,
	orderBy,
	some,
	sumBy,
	values,
} from "lodash";
import {
	DEFAULT_VAT_RATE_PERCENT_CENTS,
	lineAmountCents,
	vatMultiplier,
	vatRatePercentCents,
} from "./sale-lines";

/** One sale as the report accounts for it. A refund's amounts are negative. */
export interface DailyReportSale {
	sale: Sale;
	isRefund: boolean;
	itemsCount: number;
	totalAmountCents: number;
}

export interface DailyReportPayment {
	id: string;
	name?: string;
	count: number;
	amountCents: number;
}

export interface DailyReportVatRate {
	ratePercentCents: number;
	taxFreeAmountCents: number;
	vatAmountCents: number;
	totalAmountCents: number;
}

export interface DailyReport {
	sales: DailyReportSale[];
	salesCount: number;
	refundsCount: number;
	itemsSold: number;
	itemsRefunded: number;
	salesAmountCents: number;
	refundsAmountCents: number;
	/** Sales plus refunds: the day's takings. */
	totalAmountCents: number;
	averageBasketCents: number;
	vatRates: DailyReportVatRate[];
	payments: DailyReportPayment[];
	paymentsAmountCents: number;
	/** Takings no payment accounts for; anything but zero needs looking into. */
	unsettledAmountCents: number;
}

// The same test the API runs to put a refund's products back in stock.
const isRefundSale = (sale: Sale) =>
	some(sale.salePayments, (payment) => payment.paymentType?.isRefund);

export const paymentAmountCents = (payment: SalePayment) =>
	payment.totalAmountCents * (payment.paymentType?.isRefund ? -1 : 1);

const reportSale = (sale: Sale): DailyReportSale => {
	const isRefund = isRefundSale(sale);

	return {
		sale,
		isRefund,
		itemsCount: sumBy(sale.saleProducts, "quantity"),
		totalAmountCents: (isRefund ? -1 : 1) * sale.totalAmountCents,
	};
};

type VatAmounts = Omit<DailyReportVatRate, "ratePercentCents">;

const PRICED_FIELDS = [
	"vatRatePercentCents",
	"totalAmountCents",
	"taxFreeAmountCents",
	"vatAmountCents",
] as const;

type PricedSaleProduct = SaleProduct &
	Required<Pick<SaleProduct, (typeof PRICED_FIELDS)[number]>>;

/** Whether the API priced the line when its sale was recorded, as it now does every line. */
const isPriced = (line: SaleProduct): line is PricedSaleProduct =>
	PRICED_FIELDS.every((field) => isNumber(line[field]));

/**
 * Rounds the VAT rows estimated pro rata to whole cents, settling what rounding
 * each rate on its own leaves over on the biggest row, where a cent matters
 * least, so the rows add up to `totalAmountCents`.
 */
const roundEstimates = (
	estimated: Record<number, number>,
	totalAmountCents: number,
) => {
	const rows = map(estimated, (amountCents, rate) => ({
		ratePercentCents: Number(rate),
		totalAmountCents: Math.round(amountCents),
	}));

	const biggest = maxBy(rows, (row) => Math.abs(row.totalAmountCents));
	if (biggest) {
		biggest.totalAmountCents +=
			totalAmountCents - sumBy(rows, "totalAmountCents");
	}

	return rows;
};

/**
 * Splits the takings by VAT rate, from the VAT each line declared when its sale
 * was recorded: the API stores it on the line, the sale's promotion included,
 * so the report agrees with the day's closure to the cent and a product
 * changing rate since moves nothing.
 *
 * A sale whose lines were not priced that way falls back to spreading its total
 * over them pro rata at their products' current rates, as on the invoice.
 */
const vatRates = (sales: DailyReportSale[], totalAmountCents: number) => {
	const declared: Record<number, VatAmounts> = {};
	const estimated: Record<number, number> = {};

	const declare = (ratePercentCents: number, amounts: VatAmounts) => {
		declared[ratePercentCents] ??= {
			taxFreeAmountCents: 0,
			vatAmountCents: 0,
			totalAmountCents: 0,
		};
		const row = declared[ratePercentCents];
		row.taxFreeAmountCents += amounts.taxFreeAmountCents;
		row.vatAmountCents += amounts.vatAmountCents;
		row.totalAmountCents += amounts.totalAmountCents;
	};
	const estimate = (ratePercentCents: number, amountCents: number) => {
		estimated[ratePercentCents] =
			(estimated[ratePercentCents] ?? 0) + amountCents;
	};

	for (const { sale, isRefund, totalAmountCents: saleTotalCents } of sales) {
		const lines = sale.saleProducts ?? [];
		const linesCents = sumBy(lines, lineAmountCents);

		if (lines.length > 0 && lines.every(isPriced)) {
			// Stored positive, refunds included.
			const sign = isRefund ? -1 : 1;
			for (const line of lines) {
				declare(line.vatRatePercentCents, {
					taxFreeAmountCents: sign * line.taxFreeAmountCents,
					vatAmountCents: sign * line.vatAmountCents,
					totalAmountCents: sign * line.totalAmountCents,
				});
			}
		} else if (linesCents > 0) {
			for (const line of lines) {
				estimate(
					vatRatePercentCents(line),
					(lineAmountCents(line) / linesCents) * saleTotalCents,
				);
			}
		} else if (saleTotalCents !== 0) {
			// Nothing to split by: tax it as an ad-hoc line would be.
			estimate(DEFAULT_VAT_RATE_PERCENT_CENTS, saleTotalCents);
		}
	}

	const estimatedTotalCents =
		totalAmountCents - sumBy(values(declared), "totalAmountCents");
	for (const row of roundEstimates(estimated, estimatedTotalCents)) {
		const taxFreeAmountCents = Math.round(
			row.totalAmountCents / vatMultiplier(row.ratePercentCents),
		);
		declare(row.ratePercentCents, {
			taxFreeAmountCents,
			vatAmountCents: row.totalAmountCents - taxFreeAmountCents,
			totalAmountCents: row.totalAmountCents,
		});
	}

	return orderBy(
		map(declared, (amounts, rate) => ({
			ratePercentCents: Number(rate),
			...amounts,
		})),
		"ratePercentCents",
	);
};

/**
 * Totals by payment type, built from the payments themselves rather than the
 * store's current payment types, so a type deleted since still counts.
 */
const payments = (sales: Sale[]) => {
	const rows: Record<string, DailyReportPayment> = {};

	for (const sale of sales) {
		for (const payment of sale.salePayments ?? []) {
			const id = payment.paymentType?.id ?? "";
			rows[id] ??= {
				id,
				name: payment.paymentType?.name,
				count: 0,
				amountCents: 0,
			};
			rows[id].count += 1;
			rows[id].amountCents += paymentAmountCents(payment);
		}
	}

	return orderBy(values(rows), "name");
};

export const buildDailyReport = (sales: Sale[] = []): DailyReport => {
	const reportSales = map(sales, reportSale);
	const sold = filter(reportSales, (sale) => !sale.isRefund);
	const refunded = filter(reportSales, "isRefund");

	const salesAmountCents = sumBy(sold, "totalAmountCents");
	const refundsAmountCents = sumBy(refunded, "totalAmountCents");
	const totalAmountCents = salesAmountCents + refundsAmountCents;
	const paymentRows = payments(sales);
	const paymentsAmountCents = sumBy(paymentRows, "amountCents");

	return {
		sales: reportSales,
		salesCount: sold.length,
		refundsCount: refunded.length,
		itemsSold: sumBy(sold, "itemsCount"),
		itemsRefunded: sumBy(refunded, "itemsCount"),
		salesAmountCents,
		refundsAmountCents,
		totalAmountCents,
		averageBasketCents: sold.length
			? Math.round(salesAmountCents / sold.length)
			: 0,
		vatRates: vatRates(reportSales, totalAmountCents),
		payments: paymentRows,
		paymentsAmountCents,
		unsettledAmountCents: totalAmountCents - paymentsAmountCents,
	};
};
